import React from 'react';
import './SocialLinks.css';

// A restaurant's own social accounts, from restaurants/{id}.socials
// ({ tiktok, instagram, facebook, youtube }). Claimed restaurants edit these
// from their dashboard; renders nothing until at least one is set.
//
// Values may be full URLs or bare handles ("@districttaco"), so each is
// normalised to a profile URL on the right site — and anything that isn't
// that site is dropped rather than linked, so the field can't be used to
// send diners somewhere else.
//
// Rendered as icons rather than the platform's name spelled out: four of
// these sat in a row of word-pills that cost more height than the links were
// worth, and everyone recognises the glyphs.
const ICONS = {
  tiktok: <path d="M14 4v9.2a2.8 2.8 0 1 1-2.3-2.75V8.2A5.8 5.8 0 1 0 16.2 14V9.5A6.4 6.4 0 0 0 20 10.7V7.9A3.9 3.9 0 0 1 16.2 4z" fill="currentColor" />,
  instagram: (
    <>
      <rect x="3.2" y="3.2" width="17.6" height="17.6" rx="5" stroke="currentColor" strokeWidth="1.7" fill="none" />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.7" fill="none" />
      <circle cx="17.1" cy="6.9" r="1.2" fill="currentColor" />
    </>
  ),
  facebook: <path d="M13.3 21v-7.4h2.5l.4-2.9h-2.9V8.9c0-.8.2-1.4 1.4-1.4h1.6V4.9c-.3 0-1.2-.1-2.3-.1-2.3 0-3.8 1.4-3.8 3.9v2.1H7.7v2.9h2.5V21z" fill="currentColor" />,
  youtube: (
    <>
      <rect x="2.6" y="5.8" width="18.8" height="12.4" rx="4" stroke="currentColor" strokeWidth="1.7" fill="none" />
      <path d="M10.4 9.3l5.4 2.7-5.4 2.7z" fill="currentColor" />
    </>
  ),
};

const PLATFORMS = [
  { key: 'tiktok', label: 'TikTok', host: 'tiktok.com', profile: (h) => `https://www.tiktok.com/@${h}` },
  { key: 'instagram', label: 'Instagram', host: 'instagram.com', profile: (h) => `https://www.instagram.com/${h}/` },
  { key: 'facebook', label: 'Facebook', host: 'facebook.com', profile: (h) => `https://www.facebook.com/${h}` },
  { key: 'youtube', label: 'YouTube', host: 'youtube.com', profile: (h) => `https://www.youtube.com/@${h}` },
];

export function socialUrl(platform, value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw) || raw.includes('.')) {
    try {
      const url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
      const host = url.hostname.toLowerCase().replace(/^(www|m)\./, '');
      return host === platform.host || host.endsWith(`.${platform.host}`) ? url.href : null;
    } catch {
      return null;
    }
  }
  const handle = raw.replace(/^@/, '');
  return /^[A-Za-z0-9._-]{1,60}$/.test(handle) ? platform.profile(handle) : null;
}

export default function SocialLinks({ socials, name }) {
  const links = PLATFORMS
    .map((p) => ({ ...p, url: socialUrl(p, socials?.[p.key]) }))
    .filter((p) => p.url);
  if (links.length === 0) return null;
  return (
    <>
      {links.map((p) => (
        <a
          key={p.key}
          href={p.url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="rp-icon social-links__link"
          title={`${name} on ${p.label}`}
          aria-label={`${name} on ${p.label}`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">{ICONS[p.key]}</svg>
        </a>
      ))}
    </>
  );
}
